import React, { ReactNode, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Home,
    Wallet,
    TrendingUp,
    History,
    Receipt,
    PlusCircle,
    MinusCircle,
    Menu,
    X,
    Bell,
    User,
    Settings,
    ArrowLeftRight,
    Globe,
    MessageSquare,
    Megaphone,
    LayoutDashboard,
    Zap,
    Briefcase,
    ShieldCheck,
    Moon,
    Sun,
    ChevronRight,
    LogOut
} from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useTheme } from '@/shared/hooks/useTheme';
import { useAuth } from '@/shared/hooks/useAuth';
import { useUserAssets } from '@/shared/hooks/useUserAssets';
import { auth, db } from '@/lib/firebase';
import { signOut } from 'firebase/auth';
import UserNotifications from '@/user/components/UserNotifications';

interface LayoutProps {
    children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const { theme, toggleTheme } = useTheme();
    const { user, profile, loading } = useAuth();
    const { assets } = useUserAssets(user?.uid);

    // Redirect to login if not authenticated
    if (!loading && !user) {
        window.location.href = '/login';
        return null; // Render nothing while redirecting
    }

    const handleLogout = async () => {
        try {
            await signOut(auth);
        } catch (error) {
            console.error("Logout failed", error);
        } finally {
            localStorage.clear();
            sessionStorage.clear();
            window.location.href = '/login';
            window.location.href = '/login';
        }
    };

    // Presence / Online Status Logic
    React.useEffect(() => {
        if (!user?.uid) return;

        import('firebase/firestore').then(({ doc, updateDoc, serverTimestamp }) => {
            const userRef = doc(db, 'users', user.uid);

            // Initial update
            updateDoc(userRef, {
                lastSeen: serverTimestamp(),
                isOnline: true
            }).catch(err => console.error("Error updating presence:", err));

            // Periodic update (every 1 minute)
            const interval = setInterval(() => {
                updateDoc(userRef, {
                    lastSeen: serverTimestamp(),
                    isOnline: true
                }).catch(err => console.error("Error updating presence heartbeat:", err));
            }, 60000);

            return () => clearInterval(interval);
        });
    }, [user?.uid]);

    // Calculate total estimated value (Only KRW, no minus)
    const totalEstimatedValue = Math.max(0, assets?.krw ?? 0);

    const formatMoney = (value: number | undefined | null) => {
        if (value === undefined || value === null || isNaN(value)) return '0';
        return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
    };

    // 실제 사이트 기반 메뉴 구성
    const menuItems = [
        { icon: Home, label: '홈', path: '/home' },
        { icon: Wallet, label: '자산', path: '/wallet' },
        { icon: Receipt, label: '입출금 신청 내역', path: '/my-transactions' },
        { icon: History, label: '거래내역', path: '/history' },
        { icon: Zap, label: '데이트레이딩', path: '/day-trading' },
        { icon: Briefcase, label: '블록딜', path: '/block-deal' },
        { icon: LayoutDashboard, label: '가상자산 ETF', path: '/etf' },
        { icon: TrendingUp, label: '스테이킹', path: '/staking' },
        { icon: ArrowLeftRight, label: 'P2P 거래', path: '/p2p' },
        { icon: ShieldCheck, label: 'KYC 인증', path: '/verification' },
        { icon: Megaphone, label: '공지사항', path: '/notices' },
        { icon: MessageSquare, label: '고객센터', path: '/support' },
    ];

    const adminPath = "/kimstar";

    return (
        <div className="min-h-screen bg-background text-foreground flex font-sans transition-colors duration-300">
            {/* Global User Notifications */}
            <UserNotifications />

            {/* Sidebar - Desktop */}
            <aside className="hidden lg:flex w-72 flex-col fixed inset-y-0 border-r border-border bg-card z-50">
                <div className="p-8 pb-4 flex justify-center">
                    <div className="group cursor-pointer" onClick={() => navigate('/home')}>
                        <img src="/로고.jpg" alt="cointocash" className="w-20 h-20 object-contain group-hover:scale-105 transition-all duration-500" />
                    </div>
                </div>

                <nav className="flex-1 px-4 py-8 space-y-1.5 overflow-y-auto">
                    {menuItems.map((item) => (
                        <button
                            key={item.path}
                            onClick={() => navigate(item.path)}
                            className={cn(
                                "w-full flex items-center gap-4 px-5 py-3.5 rounded-2xl transition-all duration-300 group text-sm font-bold",
                                location.pathname === item.path
                                    ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20"
                                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                            )}
                        >
                            <item.icon className={cn("h-5 w-5 transition-transform group-hover:scale-110", location.pathname === item.path ? "text-white" : "text-muted-foreground")} />
                            <span>{item.label}</span>
                        </button>
                    ))}
                </nav>
            </aside>

            {/* Main Content Area */}
            <div className="flex-1 lg:ml-72 min-h-screen flex flex-col relative">
                {/* Header */}
                <header className="h-20 flex items-center justify-between px-6 lg:px-10 sticky top-0 bg-background/80 backdrop-blur-xl z-40 border-b border-border transition-colors">
                    <div className="flex items-center gap-4">
                        <button
                            className="lg:hidden p-2.5 rounded-xl bg-accent text-accent-foreground hover:bg-accent/80 transition-colors"
                            onClick={() => setIsMobileMenuOpen(true)}
                        >
                            <Menu className="h-6 w-6" />
                        </button>
                        <div className="hidden sm:flex flex-col">
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">자산 잔액</span>
                                <span className="bg-blue-600 text-white text-[8px] px-1 rounded font-black rounded-sm animate-pulse">V11_LIVE (02/10 10:35)</span>
                            </div>
                            <div className="flex items-center gap-2">
                                {location.pathname === '/day-trading' ? (
                                    <button
                                        onClick={() => navigate('/wallet')}
                                        className="h-8 px-3 rounded-lg bg-accent border border-border text-[10px] font-black text-muted-foreground hover:text-blue-500 hover:border-blue-500/50 transition-all uppercase tracking-widest flex items-center gap-2"
                                    >
                                        내 자산 관리 <ChevronRight className="h-3 w-3" />
                                    </button>
                                ) : (
                                    <>
                                        <span className="text-lg md:text-xl font-black tabular-nums">₩{formatMoney(totalEstimatedValue)}</span>
                                        <span className="text-xs font-bold text-emerald-500 bg-emerald-500/10 px-1.5 py-0.5 rounded-md">+2.4%</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 lg:gap-5">
                        <button
                            onClick={toggleTheme}
                            className="p-2.5 rounded-full bg-accent hover:bg-accent/80 text-accent-foreground transition-all"
                        >
                            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                        </button>

                        <div
                            className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent border border-border transition-colors cursor-pointer hover:bg-accent/80"
                            onClick={() => alert('현재 한국어만 지원됩니다. 추후 글로벌 언어가 추가될 예정입니다.')}
                        >
                            <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="text-[10px] font-bold text-muted-foreground uppercase">한국어</span>
                        </div>

                        <button className="p-2.5 rounded-full bg-accent hover:bg-accent/80 text-muted-foreground hover:text-foreground transition-all relative group">
                            <Bell className="h-5 w-5" />
                            <span className="absolute top-2.5 right-2.5 w-2 h-2 bg-red-500 rounded-full border-2 border-background group-hover:scale-125 transition-transform"></span>
                        </button>

                        <div className="relative">
                            <button
                                className="flex items-center gap-3 pl-4 border-l border-border group cursor-pointer transition-colors"
                                onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                            >
                                <div className="text-right hidden sm:block">
                                    <p className="text-xs font-black group-hover:text-blue-400 transition-colors leading-none">
                                        {assets?.name || profile?.name || user?.email?.split('@')[0] || 'User'}님
                                    </p>
                                    <div className="flex items-center gap-1 justify-end mt-1">
                                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
                                        <span className="text-[9px] text-muted-foreground uppercase font-black">접속 중</span>
                                    </div>
                                </div>
                                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-white font-black overflow-hidden shadow-lg shadow-blue-600/20 group-hover:rotate-6 transition-transform">
                                    <User className="h-5 w-5" />
                                </div>
                            </button>

                            {isProfileMenuOpen && (
                                <div className="absolute right-0 top-full mt-2 w-48 bg-card border border-border rounded-xl shadow-xl overflow-hidden z-50">
                                    <div className="p-2 space-y-1">
                                        <button
                                            onClick={() => {
                                                navigate('/verification');
                                                setIsProfileMenuOpen(false);
                                            }}
                                            className="w-full flex items-center gap-2 px-4 py-2 text-sm font-bold text-muted-foreground hover:bg-accent hover:text-foreground rounded-lg transition-colors"
                                        >
                                            <ShieldCheck className="h-4 w-4" />
                                            <span>KYC 인증</span>
                                        </button>
                                        <div className="h-px bg-border my-1"></div>
                                        <div className="px-4 py-2 space-y-1">
                                            <p className="text-[10px] text-muted-foreground font-black uppercase tracking-widest">보유 원화 (KRW)</p>
                                            <p className="text-sm font-black text-foreground">₩{formatMoney(assets?.krw)}</p>
                                        </div>
                                        <div className="h-px bg-border my-1"></div>
                                        <button
                                            onClick={handleLogout}
                                            className="w-full flex items-center gap-2 px-4 py-2 text-sm font-bold text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                                        >
                                            <LogOut className="h-4 w-4" />
                                            <span>로그아웃</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                {/* Content */}
                <main className="flex-1 p-6 lg:p-10 pb-28 lg:pb-10 max-w-7xl mx-auto w-full transition-colors duration-500">
                    {children}
                </main>

                {/* Bottom Navigation - Mobile */}
                <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-card/95 backdrop-blur-xl border-t border-border px-4 flex items-center justify-between z-50 safe-area-bottom transition-colors pb-[env(safe-area-inset-bottom)]">
                    {menuItems.slice(0, 5).map((item) => (
                        <button
                            key={item.path}
                            onClick={() => navigate(item.path)}
                            className={cn(
                                "flex flex-col items-center gap-1.5 flex-1 transition-colors relative h-full justify-center pt-2",
                                location.pathname === item.path ? "text-blue-500 font-bold" : "text-muted-foreground"
                            )}
                        >
                            <item.icon className={cn("h-6 w-6", location.pathname === item.path ? "text-blue-500 scale-110" : "text-muted-foreground")} />
                            <span className="text-[10px] font-bold">{item.label}</span>
                            {location.pathname === item.path && (
                                <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-1 bg-blue-500 rounded-full"></span>
                            )}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Mobile Drawer Overlay */}
            <div
                className={cn(
                    "lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] transition-opacity duration-300",
                    isMobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
                )}
                onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Mobile Drawer Content */}
            <div
                className={cn(
                    "lg:hidden fixed inset-y-0 left-0 w-80 bg-card z-[70] transition-transform duration-300 transform shadow-2xl",
                    isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
                )}
            >
                <div className="p-8 flex items-center justify-between border-b border-border">
                    <div className="flex-1 flex justify-center">
                        <img src="/로고.jpg" alt="cointocash" className="w-20 h-20 object-contain" />
                    </div>
                    <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 text-muted-foreground hover:text-foreground">
                        <X className="h-6 w-6" />
                    </button>
                </div>
                <div className="p-6 space-y-2 overflow-y-auto max-h-[calc(100vh-140px)]">
                    {menuItems.map((item) => (
                        <button
                            key={item.path}
                            onClick={() => {
                                navigate(item.path);
                                setIsMobileMenuOpen(false);
                            }}
                            className={cn(
                                "w-full flex items-center gap-4 px-4 py-4 rounded-2xl text-sm font-bold transition-all",
                                location.pathname === item.path ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20" : "text-muted-foreground hover:bg-accent"
                            )}
                        >
                            <item.icon className="h-5 w-5" />
                            <span>{item.label}</span>
                        </button>
                    ))}
                </div>


            </div>
        </div>
    );
};

export default Layout;
